import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Zap, Clock, CheckCircle2 } from "lucide-react";
import { Helmet } from "react-helmet";
import ChatInterface from "@/components/onboarding/ChatInterface";
import TestOptions from "@/components/onboarding/TestOptions";
import RegistrationPrompt from "@/components/onboarding/RegistrationPrompt";
import { ConversionTracking, trackConversion } from "@/components/tracking/ConversionTracking";
import { CookieConsent } from "@/components/tracking/CookieConsent";
import LiveActivityTicker from "@/components/LiveActivityTicker";
import SocialShareButtons from "@/components/SocialShareButtons";
import { SharedHeader } from "@/components/SharedHeader";

type FlowStep = 'chat' | 'test' | 'register';

const ChatToAgent = () => {
  const {
    t
  } = useTranslation();
  const [flowStep, setFlowStep] = useState<FlowStep>('chat');
  const [agentConfig, setAgentConfig] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');

  // Track flow step changes for conversions
  useEffect(() => {
    if (flowStep === 'test') {
      trackConversion('AgentCreated', {
        event_category: 'Agent Creation',
        event_label: 'Chat Method',
        value: 1
      });
    } else if (flowStep === 'register') {
      trackConversion('ReachedSignup', {
        event_category: 'Funnel',
        event_label: 'Chat Method',
        value: 2
      });
    }
  }, [flowStep]);
  return <>
      <CookieConsent />
      <ConversionTracking pageName="Chat to Agent Landing Page" />
      <Helmet>
        <title>Build AI Phone Agent by Chat | AI Receptionist Service | Call24x7</title>
        <meta name="description" content="Build AI phone agent by chatting with AI. Create your AI receptionist service in 60 seconds - no coding required. AI customer service automation for small business." />
        <meta name="keywords" content="AI customer support platform, AI receptionist service, AI customer service automation, how to automate customer calls with AI, AI tools for small business call handling, build phone agent, AI phone agent, create AI agent" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7.AI" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/chat-to-agent" />
        <meta property="og:title" content="Build AI Phone Agent by Chat | AI Receptionist Service" />
        <meta property="og:description" content="Build AI phone agent by simply chatting with AI. Create your AI receptionist service for customer service automation." />
        <meta property="og:image" content="https://call24x7.ai/og-image-chat.png" />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://call24x7.ai/chat-to-agent" />
        <meta name="twitter:title" content="Build AI Phone Agent by Chat | AI Receptionist Service" />
        <meta name="twitter:description" content="Build AI phone agent by chatting with AI. AI customer service automation in 60 seconds - no coding required." />
        <meta name="twitter:image" content="https://call24x7.ai/og-image-chat.png" />
        
        <link rel="canonical" href="https://call24x7.ai/chat-to-agent" />
        
        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Call24x7.AI Chat to Phone Agent Builder",
          "applicationCategory": "BusinessApplication",
          "description": "Build AI phone agent by chatting with AI. Create your AI receptionist service for customer service automation. Perfect AI tools for small business call handling.",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "127"
          }
        })}
        </script>
        
        {/* Structured Data - HowTo */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Automate Customer Calls with AI",
          "description": "Learn how to automate customer calls with AI using Call24x7.AI's chat interface",
          "step": [{
            "@type": "HowToStep",
            "name": "Start Conversation",
            "text": "Begin chatting with our AI assistant about your business needs for AI customer service automation"
          }, {
            "@type": "HowToStep",
            "name": "Describe Requirements",
            "text": "Tell the AI what your AI receptionist service should do and how it should respond"
          }, {
            "@type": "HowToStep",
            "name": "Review Configuration",
            "text": "The AI configures your AI customer support platform based on your conversation"
          }, {
            "@type": "HowToStep",
            "name": "Test Agent",
            "text": "Test your new AI phone agent with a real call in under 60 seconds"
          }],
          "totalTime": "PT1M"
        })}
        </script>
        
        {/* Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://call24x7.ai"
          }, {
            "@type": "ListItem",
            "position": 2,
            "name": "Build AI Phone Agent by Chat",
            "item": "https://call24x7.ai/chat-to-agent"
          }]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Live Activity Ticker */}
        <div className="fixed top-0 left-0 right-0 z-40">
          <LiveActivityTicker />
        </div>

        <SharedHeader />

        {/* Hero Section */}
        <section className="pt-28 pb-4 px-4 md:px-4">
          <div className="container mx-auto max-w-6xl px-0 md:px-4">
            <div className="text-center mb-4 px-4 md:px-0">
              <a href="https://manage.call24x7.ai/signup" className="inline-block">
                <Badge className="mb-2 bg-primary/10 text-primary border-primary/20 text-xs cursor-pointer hover:bg-primary/20 transition-colors">
                  <Zap className="w-3 h-3 mr-1" />
                  {t('hero.badge')}
                </Badge>
              </a>
              <h1 className="text-2xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent py-[3px]">
                Build Phone Agent by Chat
                <br />
                
              </h1>
              <p className="text-base text-muted-foreground max-w-3xl mx-auto mb-4">
                Describe your needs. AI builds your custom phone agent.
              </p>
            </div>

            {/* Main Content - Flow Steps */}
            <div className="max-w-4xl mx-auto px-0 md:px-4">
{flowStep === 'chat' && <ChatInterface onComplete={(config, conversationMessages, sid, aid) => {
              // Enhance config with conversation for context
              const enhancedConfig = {
                ...config,
                conversationHistory: conversationMessages
              };
              setAgentConfig(enhancedConfig);
              setSessionId(sid);
              setAgentId(aid);
              setFlowStep('test');
            }} onBack={() => window.location.href = '/'} />}

              {flowStep === 'test' && agentConfig && <div className="space-y-6">
                  <Card className="p-6 bg-primary/5 border-primary/20">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Agent Created Successfully!</h3>
                        <p className="text-muted-foreground mb-4">
                          Your AI agent "{agentConfig.name}" is ready to test.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">{agentConfig.type}</Badge>
                          {agentConfig.features?.map((feature: string, i: number) => <Badge key={i} variant="outline">{feature}</Badge>)}
                        </div>
                      </div>
                    </div>
                  </Card>

                  <TestOptions agentConfig={agentConfig} sessionId={sessionId} agentId={agentId} onRegister={(sid, aid) => {
                setSessionId(sid);
                setAgentId(aid);
                setFlowStep('register');
              }} onBack={() => setFlowStep('chat')} />
                </div>}

              {flowStep === 'register' && <RegistrationPrompt agentId={agentId} agentConfig={agentConfig} sessionId={sessionId} onComplete={() => {
              window.location.href = 'https://manage.call24x7.ai/signup';
            }} />}
            </div>

            {/* Benefits Grid */}
            {flowStep === 'chat' && <div className="grid md:grid-cols-3 gap-4 mt-6 px-4 md:px-0">
                <Card 
                  className="p-4 border-2 hover:border-primary/50 transition-colors cursor-pointer hover:shadow-lg"
                  onClick={() => window.location.href = 'https://manage.call24x7.ai/signup'}
                >
                  <MessageSquare className="w-8 h-8 text-primary mb-2" />
                  <h3 className="text-base font-semibold mb-1">Natural Conversation</h3>
                  <p className="text-sm text-muted-foreground">
                    Just chat about your business needs. No technical knowledge required.
                  </p>
                </Card>
                <Card 
                  className="p-4 border-2 hover:border-primary/50 transition-colors cursor-pointer hover:shadow-lg"
                  onClick={() => window.location.href = 'https://manage.call24x7.ai/signup'}
                >
                  <Clock className="w-8 h-8 text-primary mb-2" />
                  <h3 className="text-base font-semibold mb-1">Ready in 60 Seconds</h3>
                  <p className="text-sm text-muted-foreground">
                    Your AI agent is configured and ready to test in under a minute.
                  </p>
                </Card>
                <Card 
                  className="p-4 border-2 hover:border-primary/50 transition-colors cursor-pointer hover:shadow-lg"
                  onClick={() => window.location.href = 'https://manage.call24x7.ai/signup'}
                >
                  <Zap className="w-8 h-8 text-primary mb-2" />
                  <h3 className="text-base font-semibold mb-1">Fully Customizable</h3>
                  <p className="text-sm text-muted-foreground">
                    Fine-tune every detail through conversation until it's perfect.
                  </p>
                </Card>
              </div>}

            {flowStep === 'chat' && <div className="flex justify-center mt-6 px-4 md:px-0">
              <Button size="lg" onClick={() => window.location.href = 'https://manage.call24x7.ai/signup'}>
                {t("hero.getStarted")}
              </Button>
            </div>}

            {/* Trust Indicators */}
            {flowStep === 'chat' && <div className="mt-6 text-center px-4 md:px-0">
                <p className="text-xs text-muted-foreground mb-2">Trusted by businesses worldwide</p>
                <div className="flex flex-wrap justify-center gap-4 items-center opacity-60">
                  <span className="text-xs font-medium">Healthcare</span>
                  <span className="text-xs font-medium">E-commerce</span>
                  <span className="text-xs font-medium">Finance</span>
                  <span className="text-xs font-medium">Real Estate</span>
                  <span className="text-xs font-medium">Automotive</span>
                </div>
              </div>}
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t py-4 mt-8">
          <div className="container mx-auto px-4">
            <SocialShareButtons className="mb-4" />
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © 2024 Call24x7.AI. All rights reserved.
              </p>
              <div className="flex gap-6">
                <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Privacy Policy
                </a>
                <a href="/#pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Pricing
                </a>
                <a href="/#features" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Features
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
};
export default ChatToAgent;